/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { SimpleProduct, ItemAvailability, format } from '@msdyn365-commerce/retail-proxy';
import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxFindInStoreViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState
} from '@msdyn365-commerce-modules/buybox';
import {
    ArrayExtensions,
    getFullAvailableInventoryNearby,
    GetFullAvailableInventoryNearbyInput,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';
import { IncrementalQuantity, ITelemetryContent, Module, Node, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { reaction } from 'mobx';

import { ILobasBuyboxProps, ILobasBuyboxResources } from './lobas-buybox.props.autogenerated';
import { renderAddToOrderTemplateButton } from './components/lobas-buybox-add-to-order-template';
import { ILobasBuyboxData } from './lobas-buybox.data';
import { ILobasBuyboxViewProps } from './lobas-buybox';
import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';

/**
 * DescriptionText.
 */
/*enum DescriptionText {
    Shop = 'More ways to shop',
    Price = 'Price',
    Description = 'Description',
    Find = 'Pickup and Delivery Methods',
    SKU = 'SKU: '
}*/

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

/**
 * BuyBoxConstants enum.
 */
export enum BuyBoxConstants {
    zero = 0
}

/**
 * RenderQuantity.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @returns -- Returns.
 */
const renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: ILobasBuyboxProps<ILobasBuyboxData>,
    state: IBuyboxState,
    extentionResources: ILobasBuyboxResources,
    quantityLimitsMessages: React.ReactNode,

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    max: number | undefined,
    telemetryContent?: ITelemetryContent,
    unitOfMeasure?: React.ReactNode,
    configure?: IBuyboxProductConfigureViewProps
): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { /*ContainerProps,*/ LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    const mainDiv: INodeProps = {
        className: configure ? 'ms-buybox__quantity ms-buybox__quantity-right' : 'ms-buybox__quantity ms-buybox__quantity-left'
    };

    if (
        !props.config.hideQty ||
        (props.config.hideQty &&
            props.context.request.user.isAuthenticated &&
            ((props.context.request.user.customerAccountNumber !== null && props.context.request.user.customerAccountNumber !== '') ||
                props.context.request.user.isOBORequest))
    ) {
        return (
            <Node {...mainDiv}>
                <Node {...LabelContainerProps}>
                    {heading}
                    {errors}
                </Node>

                <IncrementalQuantity
                    id='ms-buybox__product-quantity-input'
                    max={max}
                    currentCount={quantity}
                    onChange={onChangeHandler(callbacks)}
                    inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                    decrementButtonAriaLabel='Decrease the quantity by 1'
                    incrementButtonAriaLabel='Increase quantity by 1'
                    minQuantityText={extentionResources.minQuantityText}
                    maxQuantityText={extentionResources.maxQuantityText}
                    telemetryContent={telemetryContent}
                />
                {unitOfMeasure}
                {quantityLimitsMessages}
            </Node>
        );
    } else {
        return <></>;
    }
};

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
const renderAddToCart = (props: ILobasBuyboxProps<ILobasBuyboxData>, addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    if (
        !props.config.hideAddToCart ||
        (props.config.hideAddToCart &&
            props.context.request.user.isAuthenticated &&
            ((props.context.request.user.customerAccountNumber !== null && props.context.request.user.customerAccountNumber !== '') ||
                props.context.request.user.isOBORequest))
    ) {
        return (
            <Node {...ContainerProps}>
                {errorBlock}
                {button}
            </Node>
        );
    } else {
        return <></>;
    }
};

/**
 * RenderFindInStore.
 * @param findInStore - FindInStore.
 * @returns -- Returns.
 */
const renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: ILobasBuyboxViewProps & ILobasBuyboxProps<ILobasBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, product);

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (callbacks: IBuyboxCallbacks, resources: IBuyboxCommonResources, product: SimpleProduct | undefined) => (
    result: IWishlistActionErrorResult
) => {
    callbacks.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: ILobasBuyboxViewProps & ILobasBuyboxProps<ILobasBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={resources.addToWishlistButtonText}
            removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
            addToWishlistMessage={resources.addToWishlistMessage}
            removedFromWishlistMessage={resources.removedFromWishlistMessage}
            addItemToWishlistError={resources.addItemToWishlistError}
            removeItemFromWishlistError={resources.removeItemFromWishlistError}
            nameOfWishlist={resources.nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            ariaRole='button'
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: ILobasBuyboxViewProps & ILobasBuyboxProps<ILobasBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToWishlist || !product) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);

    if (
        !props.config.hideAddToWishlist ||
        (props.config.hideAddToWishlist &&
            props.context.request.user.isAuthenticated &&
            ((props.context.request.user.customerAccountNumber !== null && props.context.request.user.customerAccountNumber !== '') ||
                props.context.request.user.isOBORequest))
    ) {
        return (
            <Node {...props.addToWishlist.ContainerProps}>
                {props.addToWishlist.errorBlock}
                {wishlistButton}
            </Node>
        );
    } else {
        return <></>;
    }
};

/**
 * RRenderShopSimilarItem.
 * @param shopSimilarItem - ShopSimilarItem.
 * @returns -- Returns.
 */
const renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

const renderLobasConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

const renderLobasConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderLobasConfigureDropdown)}</Node>;
};

const _renderPriceSection = (props: ILobasBuyboxViewProps & ILobasBuyboxProps<ILobasBuyboxData>): JSX.Element => {
    if (
        !props.config.hidePrice ||
        (props.config.hidePrice &&
            props.context.request.user.isAuthenticated &&
            ((props.context.request.user.customerAccountNumber !== null && props.context.request.user.customerAccountNumber !== '') ||
                props.context.request.user.isOBORequest))
    ) {
        return (
            <Node className='msc-buybox__price-section'>
                <div className='msc-buybox__price-section-text'>{props.resources.priceLabel}</div>
                {props.price}
            </Node>
        );
    } else {
        return <></>;
    }
};

/*const _renderSimilarItemsSection = (props: ILobasBuyboxViewProps & ILobasBuyboxProps<ILobasBuyboxData>): JSX.Element => {
    const { shopSimilarLook, shopSimilarDescription, resources } = props;

    if (!props.config.hideMoreWaysToShop || (props.config.hideMoreWaysToShop && props.context.request.user.isAuthenticated)) {
        return (
            <Node className='msc-buybox__shop-description'>
                <div className='msc-buybox__description-text'>{resources.shopLabel}</div>
                {shopSimilarLook && renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && renderShopSimilarItem(shopSimilarDescription)}
            </Node>
        );
    } else {
        return <></>;
    }
};*/

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */
const buyboxView: React.FC<ILobasBuyboxViewProps & ILobasBuyboxProps<ILobasBuyboxData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        productComparisonButton,
        configure,
        description,
        findInStore,
        quantity,
        title,
        rating,
        shopSimilarLook,
        shopSimilarDescription,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        max,
        unitOfMeasure,
        callbacks,
        state,
        resources,
        quantityLimitsMessages,
        telemetryContent,
        app: { config: appConfig },
        catalogs,
        lobasConfigure,
        inventoryLabel
    } = props;
    let skuText: string = props.resources.sKULabel;
    skuText += props.data.product.result?.ItemId;

    const preferredStoreInfo: IStoreInfo | undefined = props.data.storeSelectorStateManager.result?.preferredStore;
    const preferredStoreId = preferredStoreInfo?.StoreId ? preferredStoreInfo.StoreId : '';
    const preferredStoreName = preferredStoreInfo?.StoreName ? preferredStoreInfo.StoreName : '';

    const [availability, setAvailability] = React.useState<string>('');
    const [isStoreChange, setIsStoreChange] = React.useState<boolean>(false);

    /**
     * GetStockInventoryLabel.
     * @param availabilityWithHours - Stock availability with hours.
     * @returns -- Returns.
     */
    const getStockInventoryLabel = (availabilityWithHours: IFullOrgUnitAvailability | undefined): string | undefined => {
        if (availabilityWithHours && ArrayExtensions.hasElements(availabilityWithHours.ProductInventoryInformation)) {
            for (const productInventoryInformation of availabilityWithHours.ProductInventoryInformation) {
                if (
                    availabilityWithHours.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId ===
                    productInventoryInformation.InventLocationId
                ) {
                    return productInventoryInformation.StockLevelLabel;
                }
            }
        }

        return undefined;
    };

    const getInventory = React.useCallback(async () => {
        const input = new GetFullAvailableInventoryNearbyInput(
            props.data.product.result?.RecordId,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            true
        );

        /**
         * IsProductInStock.
         * @param itemAvailabilities - Item availability array.
         * @returns -- Returns.
         */
        const isProductInStock = (itemAvailabilities: ItemAvailability[] | undefined): boolean => {
            if (!appConfig.enableStockCheck) {
                return true;
            }

            if (ArrayExtensions.hasElements(itemAvailabilities)) {
                return itemAvailabilities[BuyBoxConstants.zero].AvailableQuantity! > BuyBoxConstants.zero;
            }

            return false;
        };

        await getFullAvailableInventoryNearby(input, props.context.actionContext)
            .catch((error: Error) => {
                props.telemetry.error(error.message);
            })
            .then(response => {
                if (response && ArrayExtensions.hasElements(response)) {
                    const isPreferredStore = (value: IFullOrgUnitAvailability) =>
                        value.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === preferredStoreId;
                    const filteredFullOrgUnitAvailability = response.filter(isPreferredStore);
                    const preferredStoreAvailability = ArrayExtensions.hasElements(filteredFullOrgUnitAvailability)
                        ? filteredFullOrgUnitAvailability[BuyBoxConstants.zero]
                        : undefined;
                    const stockStatusLabel: string | undefined = getStockInventoryLabel(preferredStoreAvailability);
                    const isInStock = isProductInStock(preferredStoreAvailability?.OrgUnitAvailability?.ItemAvailabilities);
                    const resourceLabel = isInStock ? props.resources.inStockText : props.resources.outOfStockText;
                    const stockText: string | undefined = stockStatusLabel ? stockStatusLabel : resourceLabel;
                    const preferredStoreAvailabilityText = `${format(
                        props.resources.availabilityAtPreferredStoreText,
                        preferredStoreName
                    )} ${stockText}`;
                    setAvailability(preferredStoreAvailabilityText);
                }
            });

        setIsStoreChange(false);
    }, [
        props.data.product.result?.RecordId,
        appConfig.enableStockCheck,
        preferredStoreId,
        preferredStoreName,
        props.context.actionContext,
        props.resources.availabilityAtPreferredStoreText,
        props.resources.inStockText,
        props.resources.outOfStockText,
        props.telemetry
    ]);

    React.useEffect(() => {
        if (props.config.displayStockAvailability) {
            getInventory().catch((error: Error) => {
                props.telemetry.error(error.message);
            });
        }
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability]);

    reaction(
        () => props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        () => {
            setIsStoreChange(true);
        }
    );

    /**
     * RenderAvailabiltyAtPreferredStore.
     * @returns -- Returns.
     */
    const renderAvailabilityAtPreferredStore = (): JSX.Element | undefined => {
        const hasProductDimensions = ArrayExtensions.hasElements(props.data.product.result?.Dimensions);
        if (hasProductDimensions && props.data.product.result?.MasterProductId === undefined) {
            return undefined;
        }
        const containerProps: INodeProps = {
            className: 'ms-buybox__preferred-store-availability',
            tag: 'span'
        };

        return <Node {...containerProps}>{availability}</Node>;
    };

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>
                {props.mediaGallery}
                {lobasConfigure && renderLobasConfigure(lobasConfigure)}
                {ArrayExtensions.hasElements(props.slots.productDescription) ? (
                    <Node className='lobas-buybox__product-description'>{props.slots.productDescription[0]}</Node>
                ) : null}
            </Node>
            <Node {...ProductInfoContainerProps}>
                {catalogs && catalogs[0].Name}
                {title}
                <Node className='msc-buybox__ratings-section'>
                    <div className='msc-buybox__ratings-section-sku-text'>{skuText}</div>
                    {rating}
                </Node>
                <Node className='msc-buybox__description-section'>
                    <div className='msc-buybox__description-section-text'>{resources.descriptionLabel}</div>
                    {description}
                </Node>
                {_renderPriceSection(props)}
                <Node className='msc-buybox__qtyconfig-section'>
                    {configure && renderConfigure(configure)}
                    {quantity &&
                        renderQuantity(
                            quantity,
                            callbacks,
                            props,
                            state,
                            resources,
                            quantityLimitsMessages,
                            max,
                            telemetryContent,
                            unitOfMeasure,
                            configure
                        )}
                    {(!props.config.hideInventoryStatus ||
                        (props.config.hideInventoryStatus &&
                            props.context.request.user.isAuthenticated &&
                            ((props.context.request.user.customerAccountNumber !== null &&
                                props.context.request.user.customerAccountNumber !== '') ||
                                props.context.request.user.isOBORequest))) &&
                        inventoryLabel}
                    {props.bulkPurchaseLink}
                </Node>
                {props.config.displayStockAvailability && preferredStoreId && renderAvailabilityAtPreferredStore()}
                {/* {inventoryLabel} */}
                {renderAddToCart(props, addToCart)}
                {findInStore && renderFindInStore(findInStore)}
                {addToWishlist && renderAddToWishlist(props, state, callbacks)}
                {productComparisonButton}
                {addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks)}
                {findInStore?.productPickupOptionList ? (
                    <div className='msc-buybox__pickup-options'>{findInStore.productPickupOptionList}</div>
                ) : null}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                <Node className='msc-buybox__shop-description'>
                    {shopSimilarLook && renderShopSimilarItem(shopSimilarLook)}
                    {shopSimilarDescription && renderShopSimilarItem(shopSimilarDescription)}
                </Node>
                {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                    <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
                ) : null}
            </Node>
        </Module>
    );
};

export default buyboxView;
